import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SubscriptionSection from '../components/SubscriptionSection';
import '../styles/IotCctv.css';

const IotCctv = () => {
  const [activeTab, setActiveTab] = useState('intro-iot');
  const [step, setStep] = useState(1);
  const [selectedProject, setSelectedProject] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // API configuration
  const API_BASE_URL = "https://phucansolutions.vn" ;

  // Fetch projects data
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        setLoading(true);
        setError(null);
        
        console.log('🔄 Fetching IoT & CCTV projects...');
        
        // Try to fetch projects by category first
        let response = await fetch(`${API_BASE_URL}/api/projects/category/iot-cctv?limit=6`);
        
        if (!response.ok) {
          // If category endpoint fails, try featured projects
          response = await fetch(`${API_BASE_URL}/api/projects/featured?limit=6`);
        }

        if (response.ok) {
          const data = await response.json();
          const processedProjects = data.data?.map(processProject) || [];
          setProjects(processedProjects);
          console.log('✅ IoT & CCTV projects loaded:', processedProjects.length);
        } else {
          throw new Error('Failed to fetch projects');
        }
        
      } catch (err) {
        console.error('❌ Error fetching IoT & CCTV projects:', err);
        setError(err.message);
        
        // Use fallback data if API fails
        setProjects(getFallbackProjects());
      } finally {
        setLoading(false);
      }
    };

    fetchProjects();
  }, [API_BASE_URL]);

  // Process project data from API
  const processProject = (project) => ({
    id: project.id,
    title: project.title,
    description: project.description || `Dự án ${project.title} - Hệ thống IoT & CCTV chuyên nghiệp`,
    image: getImageUrl(project.featured_image),
    technologies: project.technologies ? (typeof project.technologies === 'string' ? JSON.parse(project.technologies) : project.technologies) : ['IoT', 'CCTV', 'Smart System'],
    status: project.status || 'completed',
    featured: project.is_featured || true,
    slug: project.slug,
    client: project.client_name,
    views: project.views_count || 0
  });

  // Get image URL
  const getImageUrl = (imagePath) => {
    if (!imagePath) return '/images/cctv-samsung.jpg';
    if (imagePath.startsWith('/uploads/')) return `${API_BASE_URL}${imagePath}`;
    return imagePath;
  };

  // Fallback projects data
  const getFallbackProjects = () => [
    {
      id: 1,
      title: 'Hệ thống CCTV nhà máy Samsung',
      description: 'Triển khai hệ thống camera giám sát toàn diện cho nhà máy sản xuất với 200+ camera AI.',
      image: '/images/cctv-samsung.jpg',
      technologies: ['AI Camera', 'Face Recognition', 'Motion Detection'],
      status: 'completed',
      featured: true
    },
    {
      id: 2,
      title: 'Camera AI phân tích hành vi - Vincom',
      description: 'Hệ thống camera thông minh phân tích hành vi khách hàng và phát hiện các tình huống bất thường.',
      image: '/images/cctv-vincom.jpg',
      technologies: ['Behavior Analytics', 'Crowd Detection', 'Heat Map'],
      status: 'completed',
      featured: true
    },
    {
      id: 3,
      title: 'IoT giám sát môi trường - Ecopark',
      description: 'Hệ thống IoT giám sát chất lượng không khí, nhiệt độ, độ ẩm và các thông số môi trường.',
      image: '/images/iot-ecopark.jpg',
      technologies: ['Environmental Sensors', 'Air Quality Monitor', 'Weather Station'],
      status: 'completed',
      featured: true
    },
    {
      id: 4,
      title: 'Hệ thống an ninh thông minh - Vinhomes',
      description: 'Giải pháp an ninh tích hợp camera, cảm biến và kiểm soát ra vào cho khu đô thị cao cấp.',
      image: '/images/security-vinhomes.jpg',
      technologies: ['Smart Security', 'Access Control', 'Intrusion Detection'],
      status: 'completed',
      featured: true
    }
  ];

  const steps = [
    { title: 'BƯỚC 1: KHẢO SÁT HIỆN TRẠNG', content: 'Đánh giá yêu cầu giám sát: khu vực cần quan sát, mục đích an ninh, quy mô hệ thống. Khảo sát hạ tầng: ổ điện, đường dây, hệ thống mạng, vị trí lắp đặt camera.' },
    { title: 'BƯỚC 2: THIẾT KẾ HỆ THỐNG', content: 'Lên sơ đồ vị trí lắp đặt camera, đầu ghi, tủ điện, đường dây, thiết bị phụ trợ. Chọn loại camera phù hợp với yêu cầu cụ thể.' },
    { title: 'BƯỚC 3: LẬP DỰ TOÁN', content: 'Tính toán số lượng thiết bị, vật tư, chi phí nhân công, dự phòng phát sinh. Chọn thương hiệu phù hợp ngân sách.' },
    { title: 'BƯỚC 4: CHUẨN BỊ THI CÔNG', content: 'Gia công giá đỡ, hộp bảo vệ, đi ống, lắp đặt tủ điện, chuẩn bị dụng cụ và vật tư thi công.' },
    { title: 'BƯỚC 5: THI CÔNG HỆ THỐNG', content: 'Kéo dây nguồn, dây tín hiệu, lắp đặt tủ điện, đấu nối nguồn cho camera và các thiết bị.' },
    { title: 'BƯỚC 6: LẮP ĐẶT CAMERA', content: 'Lắp đặt camera lên vị trí đã khảo sát, căn chỉnh góc nhìn và kiểm tra hình ảnh thực tế.' },
    { title: 'BƯỚC 7: CẤU HÌNH MẠNG', content: 'Lắp đặt đầu ghi hình, ổ cứng, kết nối mạng và cấu hình truy cập từ xa.' },
    { title: 'BƯỚC 8: KIỂM TRA & NGHIỆM THU', content: 'Kiểm tra toàn bộ hệ thống, chạy thử, hướng dẫn sử dụng và bàn giao.' },
    { title: 'BƯỚC 9: BẢO TRÌ', content: 'Lên lịch bảo trì, vệ sinh, kiểm tra định kỳ và xử lý sự cố phát sinh.' }
  ];

  const handleViewDetails = (project) => {
    setSelectedProject(project);
    setIsModalOpen(true);
  };

  // Scroll to specific section
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Handle "TÌM HIỂU THÊM" button clicks
  const handleLearnMore = (type) => {
    scrollToSection('advantages-section');
  };

  const renderIotIntro = () => (
    <div className="iot-intro-content">
      <h2 className="section-title">Giải pháp cho mọi lĩnh vực IOT</h2>
      <div className="iot-grid">
        {[
          { title: 'IOT cho công nghiệp', desc: 'Tự động hóa, tối ưu hóa quy trình sản xuất và nâng cao hiệu quả vận hành bằng cách thu thập và phân tích dữ liệu theo thời gian thực.', img: '/images/Rectangle-12.png' },
          { title: 'IOT cho văn phòng', desc: 'Kết nối thông minh vào môi trường làm việc, giúp tối ưu hóa quản lý năng lượng, giám sát an ninh và cải thiện không gian làm việc.', img: '/images/Rectangle-12-1.png' },
          { title: 'IOT cho giao thông', desc: 'Giám sát và quản lý lưu lượng phương tiện, phát hiện sự cố, hỗ trợ điều phối giao thông và nâng cao an toàn đường bộ.', img: '/images/Rectangle-12-2.png' },
          { title: 'IOT cho công cộng', desc: 'Kết nối các thiết bị như đèn đường, camera, cảm biến môi trường… giúp thu thập dữ liệu liên tục và hỗ trợ ra quyết định kịp thời trong quản lý đô thị thông minh.', img: '/images/Rectangle-12-3.png' }
        ].map((item, idx) => (
          <div key={idx} className="iot-card">
            <img src={item.img} alt={item.title} className="iot-card-image" />
            <div className="iot-card-content">
              <h3>{item.title}</h3>
              <p>{item.desc}</p>
              <button className="learn-more-btn" onClick={() => handleLearnMore('industrial')}>TÌM HIỂU THÊM</button>
            </div>
          </div>
        ))}
      </div>
      
      {/* Ưu điểm của giải pháp IoT */}
      <div id="advantages-section" className="advantages-section">
        <h2 className="section-title">Ưu điểm của giải pháp</h2>
        <div className="advantages-content">
          <div className="advantage-item">
            <div className="advantage-text">
              <h3>Bảo Mật Cao</h3>
              <p>Để đảm bảo an toàn, giải pháp IoT hiện đại được tối ưu bảo mật cao với các công nghệ sau:<br/><br/><strong>Mã hóa dữ liệu (Data Encryption):</strong> giúp bảo vệ thông tin khi truyền tải giữa các thiết bị và máy chủ. Nhờ cơ chế này, ngay cả khi dữ liệu bị chặn lại bởi các cuộc tấn công mạng, kẻ xấu cũng không thể giải mã được thông tin.<br/><br/><strong>Xác thực đa yếu tố (Multi-Factor Authentication):</strong> giúp tăng cường bảo mật bằng cách yêu cầu nhiều lớp xác thực khác nhau.</p>
              <button className="read-more-btn">ĐỌC THÊM →</button>
            </div>
            <div className="advantage-image">
              <img src="/images/Rectangle-19.png" alt="IoT Security" />
            </div>
          </div>
          
          <div className="advantage-item">
            
            <div className="advantage-text">
              <h3>Hiệu Suất Tối Ưu</h3>
              <p>IoT (Internet of Things) mang lại nhiều ưu điểm vượt trội, đặc biệt trong việc tối ưu hiệu suất.<br/><br/>Đầu tiên, IoT giúp kết nối các thiết bị thông minh, tạo ra hệ sinh thái tự động hóa, giảm thiểu sự can thiệp thủ công. Nhờ khả năng thu thập và phân tích dữ liệu liên tục, các thiết bị IoT có thể dự đoán xu hướng và tối ưu hóa quy trình, từ đó tiết kiệm chi phí và thời gian.<br/><br/>Hơn nữa, IoT giúp nâng cao hiệu quả vận hành, từ việc giám sát và điều khiển các thiết bị từ xa đến tối ưu hóa tài nguyên và năng lượng, mang lại hiệu suất làm việc cao hơn và sự linh hoạt trong quản lý.</p>
            </div>
            <div className="advantage-image">
              <img src="/images/Rectangle-20.png" alt="IoT Performance" />
            </div>
          </div>
          
          <div className="advantage-item">
            <div className="advantage-text">
              <h3>Quản Lý Linh Hoạt</h3>
              <p>IoT (Internet of Things) mang lại nhiều ưu điểm vượt trội trong việc quản lý sinh hoạt hàng ngày. Với khả năng kết nối các thiết bị thông minh, IoT giúp người dùng dễ dàng điều khiển từ xa hệ thống đèn, điều hòa, camera an ninh hay các thiết bị gia dụng chỉ qua smartphone.<br/><br/>Điều này không chỉ tiết kiệm thời gian mà còn tối ưu hóa năng lượng, giảm chi phí điện năng. Bên cạnh đó, các thiết bị IoT có khả năng tự động hóa, như lập lịch bật/tắt đèn, mở rèm theo giờ cố định, hay cảnh báo rò rỉ nước và cháy nổ kịp thời, giúp nâng cao mức độ an toàn.<br/><br/>Nhờ IoT, cuộc sống trở nên tiện nghi, an toàn và thông minh hơn bao giờ hết.</p>
            </div>
            <div className="advantage-image">
              <img src="/images/Rectangle-21.png" alt="IoT Management" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderCctvIntro = () => (
    <div className="cctv-intro-content">
      <h2 className="section-title">Giải pháp cho mọi lĩnh vực CCTV</h2>
      <div className="cctv-grid">
        {[
          { title: 'Giải pháp theo ứng dụng', desc: 'Có một giải pháp thông minh và mở cho hầu hết mọi ứng dụng. Bao gồm cả ứng dụng của bạn.', img: '/images/Rectangle-13.png' },
          { title: 'Giải pháp theo công nghệ', desc: 'Từ thời tiết khắc nghiệt đến điều kiện ánh sáng đầy thách thức, công nghệ tiên tiến của chúng tôi bảo vệ con người và doanh nghiệp trong mọi hoàn cảnh.', img: '/images/Rectangle-13-1.png' },
          { title: 'Giải pháp toàn diện', desc: 'Bạn có thể yên tâm với hệ thống được thiết kế riêng theo nhu cầu của mình, sử dụng các thành phần hoàn toàn phù hợp, tất cả đều từ Axis. Phù hợp với bạn hôm nay. Và ngày mai.', img: '/images/Rectangle-13-2.png' },
          { title: 'Giải pháp theo ngành', desc: 'Phúc An cung cấp đầy đủ các giải pháp kiểm soát ra vào, âm thanh và video mạng cho nhiều ngành công nghiệp và nhu cầu của khách hàng.', img: '/images/Rectangle-13-3.png' }
        ].map((item, idx) => (
          <div key={idx} className="cctv-card">
            <img src={item.img} alt={item.title} className="cctv-card-image" />
            <div className="cctv-card-content">
              <h3>{item.title}</h3>
              <p>{item.desc}</p>
              <button className="learn-more-btn" onClick={() => handleLearnMore('industrial')}>TÌM HIỂU THÊM</button>
            </div>
          </div>
        ))}
      </div>
      
      {/* Ưu điểm của giải pháp CCTV */}
      <div className="advantages-section">
        <h2 className="section-title">Ưu điểm của giải pháp</h2>
        <div className="advantages-content">
          <div className="advantage-item">
            <div className="advantage-text">
              <h3>Giám Sát 24/7</h3>
              <p>Giải pháp giám sát liên tục 24/7 giúp đảm bảo an ninh toàn diện cho doanh nghiệp của bạn. Hệ thống hoạt động không gián đoạn, ghi nhận và lưu trữ mọi hình ảnh, sự kiện theo thời gian thực, cho phép truy xuất nhanh chóng khi cần thiết. Nhờ đó, các nguy cơ như trộm cắp, phá hoại hay sai phạm nội bộ đều được phát hiện và xử lý kịp thời.<br/><br/>Giải pháp này không chỉ bảo vệ tài sản mà còn hỗ trợ nâng cao hiệu quả quản lý, giảm thiểu rủi ro và mang lại sự yên tâm tuyệt đối cho doanh nghiệp.</p>
              <button className="read-more-btn">ĐỌC THÊM →</button>
            </div>
            <div className="advantage-image">
              <img src="/images/Rectangle-22.png" alt="Giám Sát 24/7" />
            </div>
          </div>
          
          <div className="advantage-item">
            
            <div className="advantage-text">
              <h3>Truy Cập Từ Xa</h3>
              <p>Giải pháp hỗ trợ truy cập hệ thống giám sát từ xa giúp bạn dễ dàng theo dõi mọi hoạt động của doanh nghiệp mọi lúc, mọi nơi thông qua điện thoại, máy tính bảng hoặc máy tính. Dù bạn đang ở văn phòng, đi công tác hay ở bất kỳ đâu, vẫn có thể giám sát tình hình an ninh và vận hành theo thời gian thực.<br/><br/>Điều này giúp bạn chủ động xử lý sự cố, tiết kiệm thời gian và nâng cao hiệu quả quản lý. Với truy cập từ xa, doanh nghiệp duy trì sự linh hoạt, kiểm soát tốt hơn và luôn sẵn sàng ứng phó trong mọi tình huống.</p>
            </div>
            <div className="advantage-image">
              <img src="/images/Rectangle-23.png" alt="Truy Cập Từ Xa" />
            </div>
          </div>
          
          <div className="advantage-item">
            <div className="advantage-text">
              <h3>AI Thông Minh</h3>
              <p>Giải pháp ứng dụng trí tuệ nhân tạo mang đến khả năng phân tích dữ liệu lớn trong thời gian ngắn, giúp phát hiện các mẫu bất thường và dự đoán các xu hướng tiềm ẩn. AI hỗ trợ ra quyết định chính xác, thông minh, và kịp thời, từ đó tối ưu hóa hiệu suất và giảm thiểu sai sót.<br/><br/>Giải pháp này còn giúp doanh nghiệp nâng cao hiệu quả quản lý, tự động hóa các quy trình phức tạp, và phát hiện sớm các rủi ro tiềm ẩn.<br/><br/>Việc áp dụng AI giúp doanh nghiệp không chỉ tiết kiệm thời gian mà còn gia tăng khả năng cạnh tranh, cải thiện chất lượng dịch vụ và tối ưu hóa chi phí vận hành.</p>
            </div>
            <div className="advantage-image">
              <img src="/images/Rectangle-24.png" alt="AI Thông Minh" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderProjects = () => (
    <div className="projects-content">
      <h2 className="section-title">Dự án tiêu biểu</h2>
      
      {/* API Status Indicator */}
      {error && (
        <div className="api-status error">
          ⚠️ Đang sử dụng dữ liệu mẫu (API: {error})
        </div>
      )}
      
      {loading ? (
        <div className="loading-projects">
          <div className="loading-spinner"></div>
        </div>
      ) : (
        <div className="projects-grid">
          {projects.map((project) => (
            <div key={project.id} className="project-card">
              <img src={project.image} alt={project.title} className="project-image" />
              <div className="project-content">
                <h3>{project.title}</h3>
                <p>{project.description}</p>
                <div className="project-tech">
                  {project.technologies.slice(0, 3).map((tech, idx) => (
                    <span key={idx} className="tech-tag">{tech}</span>
                  ))}
                  {project.technologies.length > 3 && (
                    <span className="tech-tag extra">+{project.technologies.length - 3}</span>
                  )}
                </div>
                <div className="project-footer">
                  <span className="project-status">
                    {project.status === 'completed' ? '✅ Hoàn thành' : 
                     project.status === 'in-progress' ? '🔄 Đang thực hiện' : '📋 Lên kế hoạch'}
                  </span>
                  <button onClick={() => handleViewDetails(project)} className="view-details-btn">
                    Xem chi tiết →
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      
      {/* Video Section */}
      <div className="video-section">
        <h3 className="video-title">Video dự án tiêu biểu</h3>
        <div className="video-container">
          <div className="video-player">
            <iframe
              src="https://www.youtube.com/embed/LOLiq4w7SEs"
              title="Hệ thống điện mặt trời gia đình 5kW"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              className="video-iframe"
            />
          </div>
        </div>
      </div>
      
      {/* Partners Section */}
      <div className="partners-section">
        <h3 className="partners-title">Đối tác hàng - Đối tác tin cậy</h3>
        <div className="partners-grid">
          {[
            'DAHUA.png', 'Ezviz.png', 'hik.png', 'haki.png', 'imou.png', 
            'kb.png', 'tapo.png', 'Tiandy.png', 'unv.png'
          ].map((partner, idx) => (
            <div key={idx} className="partner-logo">
              <img src={`/images/${partner}`} alt={`Partner ${idx + 1}`} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'intro-iot': return renderIotIntro();
      case 'intro-cctv': return renderCctvIntro();
      case 'quy-trinh': return (
        <div className="process-content">
          <h2 className="section-title">Quy trình lắp đặt hệ thống camera quan sát</h2>
          <p className="process-description">Quy trình lắp đặt hệ thống camera quan sát: từ thiết kế hệ thống đến kiểm tra và hoàn thiện. Cuối cùng, hệ thống còn cho phép điều khiển từ xa, đáp ứng linh hoạt trong việc quản lý an ninh đủ ở bất cứ đâu.</p>
          <div className="process-timeline">
            {[
              { title: 'BƯỚC 1: KHẢO SÁT HIỆN TRẠNG', icon: '/images/ICON-1.png', content: 'Đánh giá yêu cầu giám sát: khu vực cần quan sát, mục đích an ninh, quy mô hệ thống, giám sát liên tục/sự kiện... Khảo sát hạ tầng: ổ điện, đường dây, đường ống, đèn, hệ thống mạng (LAN), vị trí lắp đặt camera (tường, trần, cột). Chỉ đo các yếu tố môi trường: ánh sáng, bụi, báo động, nhiệt độ, khả năng chống nước (nâng cấp chuẩn IP).', image: '/images/Rectangle-19.png' },
              { title: 'BƯỚC 2: THIẾT KẾ HỆ THỐNG', icon: '/images/ICON-2.png', content: 'Lên sơ đồ vị trí lắp đặt camera, đầu ghi, tủ điện, đường dây, thiết bị phụ trợ. Chọn loại camera phù hợp (trong nhà/ngoài trời, cố định/quay quét, độ phân giải, góc nhìn, hồng ngoại, v.v.).', image: '' },
              { title: 'BƯỚC 3: LẬP DỰ TOÁN VÀ CHỌN THIẾT BỊ', icon: '/images/ICON-3.png', content: 'Tính toán số lượng thiết bị, vật tư, chi phí nhân công, dự phòng phát sinh. Chọn thương hiệu, model camera, đầu ghi, ổ cứng, phụ kiện phù hợp ngân sách.', image: '' },
              { title: 'BƯỚC 4: CHUẨN BỊ THI CÔNG Cơ KHÍ', icon: '/images/ICON-4.png', content: 'Gia công giá đỡ, hộp bảo vệ, đi ống, lắp đặt tủ điện, chuẩn bị dụng cụ, vật tư thi công.', image: '' },
              { title: 'BƯỚC 5: THI CÔNG HỆ THỐNG CẤP VÀ NGUỒN', icon: '/images/ICON-5.png', content: 'Kéo dây nguồn, dây tín hiệu, lắp đặt tủ điện, đấu nối nguồn cho camera, đầu ghi, thiết bị phụ trợ.', image: '' },
              { title: 'BƯỚC 6: LẮP ĐẶT VÀ CĂN CHỈNH CAMERA', icon: '/images/ICON-6.png', content: 'Lắp đặt camera lên vị trí đã khảo sát, căn chỉnh góc nhìn, kiểm tra hình ảnh thực tế.', image: '' },
              { title: 'BƯỚC 7: LẮP ĐẶT ĐẦU GHI VÀ CẤU HÌNH MẠNG', icon: '/images/ICON-7.png', content: 'Lắp đặt đầu ghi hình, ổ cứng, kết nối mạng LAN/Internet, cấu hình truy cập từ xa, điện thoại, máy tính.', image: '' },
              { title: 'BƯỚC 8: KIỂM TRA, CHẠY THỬ VÀ NGHIỆM THU', icon: '/images/ICON-8.png', content: 'Kiểm tra toàn bộ hệ thống, chạy thử, hướng dẫn sử dụng, bàn giao nghiệm thu.', image: '' },
              { title: 'BƯỚC 9: BẢO TRÌ - BẢO DƯỠNG ĐỊNH KỲ', icon: '/images/ICON-9.png', content: 'Lên lịch bảo trì, vệ sinh, kiểm tra định kỳ, xử lý sự cố phát sinh, nâng cấp hệ thống khi cần.', image: '' }
            ].map((s, idx) => (
              <div key={idx} className={`process-step ${step === idx + 1 ? 'active' : ''}`}>
                <div className={`step-wrapper ${idx % 2 === 0 ? 'left' : 'right'}`}>
                  <button onClick={() => setStep(idx + 1)} className="step-button">
                    <img src={s.icon} alt={s.title} className="step-icon" />
                    <span className="step-title">{s.title}</span>
                  </button>
                  <div className="timeline-dot"></div>
                </div>
                {step === idx + 1 && (
                  <div className="step-detail">
                    <div className="step-detail-content">
                      <h3>{s.title}</h3>
                      <p>{s.content}</p>
                    </div>
                    {s.image && (
                      <div className="step-detail-image">
                        <img src={s.image} alt={s.title} />
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      );
      case 'du-an': return renderProjects();
      default: return null;
    }
  };

  return (
    <main className="iot-cctv-main">
      {/* Hero Banner */}
      <div className="hero-banner">
        <img src="/images/banner1.jpg" alt="IOT & CCTV Banner" className="hero-image" />
        <div className="hero-overlay"></div>
        <div className="hero-content">
          <h1>IOT & CCTV</h1>
          <p>Đây là một thiết bị, giống như tivi, camera an ninh hoặc thiết bị tập thể dục do được trao cho khả năng điện toán. Thiết bị này thu thập dữ liệu từ môi trường ...</p>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="tab-navigation">
        <button 
          onClick={() => setActiveTab('intro-iot')}
          className={`tab-btn ${activeTab === 'intro-iot' ? 'active' : ''}`}
        >
          GIỚI THIỆU IOT
        </button>
        <button 
          onClick={() => setActiveTab('intro-cctv')}
          className={`tab-btn ${activeTab === 'intro-cctv' ? 'active' : ''}`}
        >
          GIỚI THIỆU CCTV
        </button>
        <button 
          onClick={() => setActiveTab('quy-trinh')}
          className={`tab-btn ${activeTab === 'quy-trinh' ? 'active' : ''}`}
        >
          QUY TRÌNH
        </button>
        <button 
          onClick={() => setActiveTab('du-an')}
          className={`tab-btn ${activeTab === 'du-an' ? 'active' : ''}`}
        >
          DỰ ÁN TIÊU BIỂU
        </button>
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {renderTabContent()}
      </div>

      {/* Project Modal */}
      {isModalOpen && selectedProject && (
        <div className="modal-overlay" onClick={() => setIsModalOpen(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setIsModalOpen(false)}>×</button>
            <img src={selectedProject.image} alt={selectedProject.title} className="modal-image" />
            <div className="modal-body">
              <h2>{selectedProject.title}</h2>
              <p>{selectedProject.description}</p>
              <div className="modal-tech">
                {selectedProject.technologies.map((tech, idx) => (
                  <span key={idx} className="tech-tag">{tech}</span>
                ))}
              </div>
              {selectedProject.client && (
                <div className="project-info">
                  <p><strong>Khách hàng:</strong> {selectedProject.client}</p>
                </div>
              )}
              {selectedProject.views > 0 && (
                <div className="project-stats">
                  <p>👁️ {selectedProject.views} lượt xem</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* Subscription Section */}
      <SubscriptionSection />
    </main>
  );
};

export default IotCctv; 